import { useEffect } from 'react';
import { useVoiceInput } from '../hooks/useVoiceInput';

function MicIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"
      />
    </svg>
  );
}

function SpinnerIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
      className="mic-spinner"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeDasharray="14 28"
      />
    </svg>
  );
}

export function MicButton({
  onTranscript,
  disabled,
}: {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}): JSX.Element {
  const mic = useVoiceInput();

  useEffect(() => {
    if (mic.transcript && mic.transcript.trim().length > 0) {
      onTranscript(mic.transcript.trim());
    }
    // Only react to transcript changes; onTranscript is stable by contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mic.transcript]);

  const busy = mic.state === 'recording' || mic.state === 'uploading';
  const isDisabled = !!disabled || (mic.otherMicActive && !busy);

  const handleClick = (): void => {
    if (isDisabled) return;
    if (mic.state === 'idle' || mic.state === 'error') {
      void mic.start();
    } else if (mic.state === 'recording') {
      void mic.stop();
    }
    // 'uploading' → ignore clicks until complete.
  };

  const label =
    mic.state === 'recording'
      ? 'Stop recording'
      : mic.state === 'uploading'
        ? 'Transcribing…'
        : 'Record voice input';

  const className =
    'mic-btn' +
    (mic.state === 'recording' ? ' mic-btn-recording' : '') +
    (mic.state === 'uploading' ? ' mic-btn-uploading' : '') +
    (mic.state === 'error' ? ' mic-btn-error' : '');

  return (
    <button
      type="button"
      className={className}
      aria-label={label}
      title={mic.error ? `Mic error: ${mic.error}` : label}
      onClick={handleClick}
      disabled={isDisabled || mic.state === 'uploading'}
    >
      {mic.state === 'uploading' ? <SpinnerIcon /> : <MicIcon />}
    </button>
  );
}
